// src/components/Paintings.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "./paintings.css";
import AudioFloat from "./AudioFloat.js";
/* -------------------------------------
   Force dark ink navbar (black text)
------------------------------------- */
function useLightNav(flag = true) {
  useEffect(() => {
    const el = document?.body;
    if (!el) return;
    if (flag) el.setAttribute("data-light-nav", "true");
    return () => el.removeAttribute("data-light-nav");
  }, [flag]);
}

/* -------------------------------------
   Detect mobile vs desktop
------------------------------------- */
function useIsMobile(query = "(max-width: 991px)") {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = (e) => setIsMobile(e.matches);
    mql.addEventListener ? mql.addEventListener("change", on) : mql.addListener(on);
    return () => {
      mql.removeEventListener ? mql.removeEventListener("change", on) : mql.removeListener(on);
    };
  }, [query]);

  return isMobile;
}

/* -------------------------------------
   Reveal on scroll
------------------------------------- */
function useRevealOnScroll(deps = []) {
  useEffect(() => {
    const nodes = document.querySelectorAll(".paintings-page .paint-reveal");
    if (!nodes.length) return;

    if (!("IntersectionObserver" in window)) {
      nodes.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "10% 0px -4% 0px" }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
}

/* -------------------------------------
   Component
------------------------------------- */
export default function Paintings({ fetchPaintings, paintings }) {
  // navbar ink = black on this light page
  useLightNav(true);

  const isMobile = useIsMobile();

  // Framing preview single style (no mat-size selector anymore)
  const [framed, setFramed] = useState(false);

  // desktop columns (2 or 3). mobile is always single
  const [desktopCols, setDesktopCols] = useState(3);

  // load data
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof fetchPaintings === "function") fetchPaintings();
  }, [fetchPaintings]);

  // sort: available first, then sold
  const list = useMemo(() => {
    if (!paintings) return [];
    const arr = Array.isArray(paintings)
      ? paintings
      : Object.values(paintings);

    const available = arr.filter((p) => !p?.sold);
    const sold = arr.filter((p) => p?.sold);
    return [...available, ...sold];
  }, [paintings]);

  // orientation tagging on load
  const handleImgLoad = useCallback((e) => {
    const img = e.currentTarget;
    img.classList.add("is-loaded");

    const card = img.closest(".paint-card");
    const slot = img.closest(".paint-cardlink");

    if (card) {
      const { naturalWidth: w, naturalHeight: h } = img;
      const isLandscape = w >= h;

      if (slot) {
        slot.classList.toggle("is-landscape", isLandscape);
        slot.classList.toggle("is-portrait", !isLandscape);
        slot.setAttribute(
          "data-shape",
          isLandscape ? "landscape" : "portrait"
        );
      }

      card.classList.toggle("is-landscape", isLandscape);
      card.classList.toggle("is-portrait", !isLandscape);
      card.setAttribute(
        "data-shape",
        isLandscape ? "landscape" : "portrait"
      );
    }
  }, []);

  // trigger fade-in
  useRevealOnScroll([list, framed, desktopCols, isMobile]);

  // grid classes
  const gridClasses = [
    "paint-grid",
    isMobile
      ? "pm-one"
      : desktopCols === 2
      ? "pd-two"
      : "pd-three",
    framed ? "frame-on" : "frame-off",
    "mat-standard", // always standard mat sizing now
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="main-body paintings-page">
      <div className="main-page page-pad smaller black top-drop">
        {/* Header */}
        <header className="paint-head paint-reveal">
          <h1 className="paint-title-centered">PAINTINGS</h1>
          <p className="paint-sub">
            All works were created by Lorran Varden Lima in his Manhattan 
            studio. Each piece is one-of-one. Tap any work to view scale,
            medium, and acquisition details.
          </p>
        </header>

        {/* Controls row */}
        <div
          className="paint-controls paint-reveal"
          role="group"
          aria-label="View and frame controls"
        >
          {/* Desktop column toggle */}
          {!isMobile && (
            <div className="paint-view" role="group" aria-label="Desktop columns">
              <span className="paint-view-label">View</span>
              <button
                type="button"
                onClick={() => setDesktopCols(2)}
                className={
                  desktopCols === 2
                    ? "paint-view-spn paint-selected-btn"
                    : "paint-view-spn"
                }
                aria-pressed={desktopCols === 2}
              >
                2
              </button>
              <button
                type="button"
                onClick={() => setDesktopCols(3)}
                className={
                  desktopCols === 3
                    ? "paint-view-spn paint-selected-btn"
                    : "paint-view-spn"
                }
                aria-pressed={desktopCols === 3}
              >
                3
              </button>
            </div>
          )}

          {/* Frame preview */}
          <label className="paint-frame-toggle" aria-label="Framing preview toggle">
            <input
              type="checkbox"
              checked={framed}
              onChange={(e) => {
                setFramed(e.target.checked);
              }}
            />
            <span>Framing Preview</span>
          </label>
        </div>

        {/* GRID */}
        <div className={gridClasses}>
          {list.map((p) => {
            const { _id, imageURL, series, title, size, price, sold } = p || {};
            if (!imageURL) return null;

            // price -> SOLD / numeric / string / default
            let priceText = "";
            if (sold) {
              priceText = "SOLD";
            } else if (typeof price === "number") {
              const asInt = parseInt(String(price), 10);
              priceText = `$${asInt.toLocaleString("en-US")}`;
            } else if (typeof price === "string" && price.trim().length) {
              priceText = price;
            } else {
              priceText = "Inquire for price";
            }

            return (
              <Link
                to={`/paintings/${_id}`}
                key={_id || imageURL}
                className="paint-cardlink"
                data-shape=""
              >
                <article
                  className={[
                    "paint-card",
                    "paint-reveal",
                    framed ? "has-frame" : "",
                    sold ? "is-sold" : "",
                  ].join(" ")}
                >
                  <div className="paint-media">
                    <div className="paint-media-inner">
                      {sold ? <div className="paint-sold-badge">SOLD</div> : null}
                      <img
                        className="paint-img"
                        src={imageURL}
                        alt={title || "Painting"}
                        loading="lazy"
                        decoding="async"
                        onLoad={handleImgLoad}
                        onError={handleImgLoad}
                      />
                    </div>
                  </div>

                  <div className="paint-meta">
                    {series ? (
                      <div className="paint-series paint-text-gray">{series}</div>
                    ) : null}

                    {title ? (
                      <div className="paint-title-case">{title}</div>
                    ) : null}

                    <div className="paint-lines">
                      {size ? (
                        <div className="paint-location paint-text-gray-2">
                          {size}
                        </div>
                      ) : null}

                      {priceText ? (
                        <div className="paint-year italic">{priceText}</div>
                      ) : null}
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}

          {!list.length && (
            <div
              className="paint-empty"
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                opacity: 0.7,
              }}
            >
              No works found.
            </div>
          )}
        </div>
      </div>

       {/* AudioFloat: always render now */}
            <AudioFloat
              srcs={[
                {
                  src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/shop.mp3",
                  type: "audio/mpeg",
                },
              ]}
              startMuted={false}
              startVolume={0.5}
              loop={true}
              label="Site background audio"
              startTime={190}
            />
    </div>
  );
}
